import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import AIContentMessagePreview from 'notebook/ai-tools/AIContentMessagePreview';
import AIModalFileChooser, {AIModalSelectedFile} from 'notebook/ai-tools/AIModalFileChooser';
import getNotePartsFromNotebookContentMessage from 'notebook/ai-tools/getNotePartsFromNotebookContentMessage';
import {GenerateDocumentContentMessage} from 'notebook/ai-tools/NotebookAIToolsConstants';
import {generateDocumentContent} from 'notebook/ai-tools/NotebookAIToolsDAO';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import {isEnterKey} from 'util/Keyboard';

export interface AIContentInsertModalProps {
  entryId: string;
  notePartIndex: number;
  onRequestClose: () => void;
  onRequestSubmit: (args: {message: GenerateDocumentContentMessage}) => void;
}

/**
 * Modal allowing the user to describe AI-generated content, preview it, and insert it into the
 * notebook.
 */
export default function AIContentInsertModal({
  entryId,
  notePartIndex,
  onRequestSubmit,
  onRequestClose,
}: AIContentInsertModalProps): JSX.Element {
  const [promptText, setPromptText] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<Array<AIModalSelectedFile>>([]);
  // As soon as the user submits the prompt, no more interactions are possible.
  const [isSubmitted, setSubmitted] = useState<boolean>(false);
  const [resultMessage, setResultMessage] = useState<GenerateDocumentContentMessage | null>(null);

  const isLoading = isSubmitted && resultMessage == null;
  const hadEmptyResponse =
    resultMessage != null && getNotePartsFromNotebookContentMessage(resultMessage).length === 0;

  useEffect(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_AI_INSERT_CONTENT_MODAL);
  }, []);

  async function handleSubmitPrompt(): Promise<void> {
    if (isSubmitted) {
      return;
    }
    setSubmitted(true);
    const fileS3Locations = await Promise.all(
      selectedFiles.map(async ({file}) => (await ImportUtil.upload(file))!)
    );
    const result = await generateDocumentContent(entryId, fileS3Locations, promptText, notePartIndex);
    setResultMessage(result.message);
  }

  return (
    <Modal
      title="Notebook writer"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        if (hadEmptyResponse) {
          onRequestClose();
        } else {
          trackEvent(ELN_ANALYTICS_ACTIONS.INSERTED_AI_GENERATED_CONTENT);
          onRequestSubmit({message: resultMessage!});
        }
      }}
      buttons={[
        {
          // If the result text exists but there is no text to insert, change the name to "Close"
          // and make the button simply close the modal.
          text: hadEmptyResponse ? 'Close' : 'Insert',
          submit: true,
          disabled: resultMessage == null,
        },
      ]}
      footer={
        <div>
          Have feedback?{' '}
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSfQ2GqGJF5b8gOzM_a2X9_Mc-9j2NhhlaHUiXIg5sGGRGn1Sw/viewform"
            target="_blank"
            rel="noopener noreferrer"
          >
            Let us know
          </a>
          .
        </div>
      }
    >
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={
          BenchlingConfig.LLM_SHOW_HUMAN_REVIEW_DISCLAIMER
            ? 'This feature is powered by AI, and may make mistakes. A human may review inputs and outputs to improve the feature.'
            : 'This feature is powered by AI, and may make mistakes. Always verify the accuracy of AI-generated content.'
        }
        supportingInfo={
          <span>
            To generate the requested content, the AI system will analyze the content of your entry.
            Attachments and linked items are not yet supported. See the{' '}
            <a
              href="https://help.benchling.com/hc/en-us/articles/25722260980877"
              target="_blank"
              rel="noopener noreferrer"
            >
              help article
            </a>{' '}
            for more details. For more information on AI security and privacy, look{' '}
            <a
              href="https://help.benchling.com/hc/en-us/articles/20901323667853-Security-and-privacy-for-LLMs-and-AI-powered-features"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </span>
        }
        action={{actionType: InlineBannerActionType.ACCORDION}}
      />
      <div className={css(styles.promptArea)}>
        <div className={css(styles.promptInputArea)}>
          <TextInput
            placeholder="Describe what to summarize or what question to answer."
            className={css(styles.textArea)}
            value={promptText}
            onChange={(e) => {
              setPromptText(e.target.value);
            }}
            disabled={isSubmitted}
            autoFocus={true}
            onKeyDown={(e) => {
              if (isEnterKey(e)) {
                e.preventDefault();
                handleSubmitPrompt();
              }
            }}
          />
          <AIModalFileChooser
            selectedFiles={selectedFiles}
            onSetSelectedFiles={setSelectedFiles}
            isSubmitted={isSubmitted}
          />
        </div>
        <span className={css(styles.runButton)}>
          <Button
            buttonStyle={ButtonStyle.SUCCESS}
            size={ButtonSize.MEDIUM}
            icon="play"
            disabled={isSubmitted}
            onClick={handleSubmitPrompt}
          />
        </span>
      </div>
      {!isSubmitted && (
        <div className={css(styles.explanation)}>
          Try asking for...
          <ul>
            <li>Summarize the entry in a few brief sentences</li>
            <li>What protocol steps should I perform next?</li>
            <li>Summarize the materials and methods of the attached paper</li>
          </ul>
        </div>
      )}
      {isLoading && <CenteredHorizontalThrobber />}
      {resultMessage != null && <AIContentMessagePreview message={resultMessage} />}
      {hadEmptyResponse && (
        <div className={css(styles.emptyResponseMessage)}>Note: No content will be inserted.</div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  textArea: {
    marginTop: 10,
    marginBottom: 10,
  },
  promptArea: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  promptInputArea: {
    flex: 1,
  },
  runButton: {
    marginLeft: 10,
  },
  emptyResponseMessage: {
    marginTop: 6,
    fontStyle: 'italic',
    fontWeight: 'bold',
  },
  explanation: {
    marginTop: 6,
  },
});
